import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Loader2, ShieldCheck, ArrowRight } from "lucide-react";
import { Layout } from "@/components/Layout";
import { api } from "@/lib/api";

export default function CategoriesIndex() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/categories/list")
      .then(({ data }) => setCategories(data.categories))
      .catch(() => setCategories([]))
      .finally(() => setLoading(false));
  }, []);

  const title = "Business Categories in India | IndiaBusinessCircle";
  const desc =
    "Browse verified Indian businesses by category — manufacturing, IT, wholesale, logistics, healthcare and more. Discover and connect with GST-verified partners on IndiaBusinessCircle.";

  return (
    <Layout>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={`${window.location.origin}/categories`} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
      </Helmet>

      <section className="bg-slate-50 border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-14">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Explore by Industry</span>
          <h1 className="mt-3 font-display font-black tracking-tight text-3xl sm:text-4xl text-slate-900">
            Business Categories
          </h1>
          <p className="mt-3 text-slate-600 max-w-xl">
            Find and connect with GST-verified businesses across India, organised by industry.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-10">
        {loading ? (
          <div className="min-h-[30vh] flex items-center justify-center">
            <Loader2 className="animate-spin text-brand" size={32} />
          </div>
        ) : (
          <div data-testid="categories-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {categories.map((c) => (
              <Link
                key={c.slug}
                to={`/categories/${c.slug}`}
                data-testid={`category-link-${c.slug}`}
                className="group flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-brand"
              >
                <div>
                  <h2 className="font-display font-bold text-lg text-slate-900 leading-snug group-hover:text-brand transition-colors">
                    {c.name}
                  </h2>
                  <p className="mt-1.5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#166534]">
                    <ShieldCheck size={14} className="text-[#22C55E]" />
                    {c.count} verified
                  </p>
                </div>
                <ArrowRight size={20} className="text-slate-300 group-hover:text-brand group-hover:translate-x-1 transition-all shrink-0" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
